import React, { useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "@reach/router";
import { getImage, ImageDataLike } from "gatsby-plugin-image";

import {
    userPanel,
    leftWrapper,
    rightWrapper,
    textContent,
    buttonGenerate,
    mobileAccountSections,
    mobileAccordion,
    mobileAccordionButton,
    mobileAccordionButtonActive,
    mobileAccordionIcon,
    mobileAccordionPanel,
    mobileAccordionPanelOpen,
    mobileAccordionPanelInner,
    mobileSurveyShortcut,
    mobileSurveyShortcutText,
    mobileSurveyShortcutButton,
    mobileProfileAvatar,
    mobileProfileAvatarImage,
    mobileProfileAvatarFallback,
    desktopStylizations,
} from "./user-panel.module.scss";
import { selectUserProfile } from "../../../redux/user/user.selectors";

import TwoBoxSection from "../../../components/organisms/sections/common/two-box-section";
import ButtonWand from "../../../components/molecules/button-wand";
import { getAbsolutePath } from "../../utility/routes.utility";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";
import Link from "../../../components/atoms/link";
import useSurveySession from "../../hooks/use-survey-session";
import Stylizations from "./stylizations";
import ProductFavorites from "./product-favorites";
import AccountData from "./account-data";
import PassChange from "./pass-change";
import Newsletter from "./newsletter";
import { normalizeApiFileUrl } from "../../../utils/api-url";

export default function UserPanel(props: RouteComponentProps) {
    const profile = useSelector(selectUserProfile);
    const { t } = useI18next();
    const { firstSessionId } = useSurveySession();
    const [openSection, setOpenSection] = useState("stylizations");

    const title = profile ? `Cześć ${profile?.firstName}!` : "Cześć!";
    const profileInitial = profile?.firstName?.charAt(0) || "";
    const profileAvatarUri = normalizeApiFileUrl(profile?.avatarUri);
    const surveyPath = firstSessionId
        ? getAbsolutePath("ACCOUNT_SURVEY_SESSION", undefined, firstSessionId)
        : getAbsolutePath("ACCOUNT_SURVEY_SESSION");
    const mobileSections = [
        {
            id: "stylizations",
            title: "Twoje stylizacje",
            content: <Stylizations />,
        },
        {
            id: "favorites",
            title: "Ulubione ciuchy",
            content: <ProductFavorites />,
        },
        {
            id: "survey",
            title: "Twoja ankieta",
            content: (
                <div className={mobileSurveyShortcut}>
                    <p className={mobileSurveyShortcutText}>
                        Kontynuuj ankietę stylu i aktualizuj odpowiedzi, kiedy coś się zmieni.
                    </p>
                    <Link to={surveyPath} className={mobileSurveyShortcutButton}>
                        Otwórz ankietę
                    </Link>
                </div>
            ),
        },
        {
            id: "account",
            title: "Dane konta",
            content: <AccountData />,
        },
        {
            id: "password",
            title: "Zmiana hasła",
            content: <PassChange />,
        },
        {
            id: "newsletter",
            title: "Newsletter",
            content: <Newsletter />,
        },
    ];

    return (
        <>
            <div className={desktopStylizations}>
                <Stylizations />
            </div>
            <TwoBoxSection
                className={userPanel}
                classNameLeftWrapper={leftWrapper}
                classNameRightWrapper={rightWrapper}
                title={title}
                text={<div className={textContent}>
                    <div className={mobileProfileAvatar}>
                        {profileAvatarUri ? (
                            <img
                                className={mobileProfileAvatarImage}
                                src={profileAvatarUri}
                                alt={profile?.firstName ? `Zdjęcie profilowe ${profile.firstName}` : "Zdjęcie profilowe"}
                            />
                        ) : (
                            <span className={mobileProfileAvatarFallback}>
                                {profileInitial}
                            </span>
                        )}
                    </div>
                    <p>
                        Oto twoje konto, tutaj znajdziesz wszystkie stylówki oraz informacje
                        dotyczące Twojego konta. Zapraszam.
                    </p>
                    <p>Miłej zabawy!</p>
                    <ButtonWand className={buttonGenerate} link={getAbsolutePath("GENERATOR_NEW_STYLIZATION")} text={t("stylizations.button.generate")}></ButtonWand>
                </div>} />
            <div className={mobileAccountSections}>
                {mobileSections.map(section => {
                    const isOpen = openSection === section.id;
                    return (
                        <section key={section.id} className={mobileAccordion}>
                            <button
                                type="button"
                                className={`${mobileAccordionButton} ${
                                    isOpen ? mobileAccordionButtonActive : ""
                                }`}
                                aria-expanded={isOpen}
                                onClick={() => setOpenSection(isOpen ? "" : section.id)}
                            >
                                <span>{section.title}</span>
                                <span className={mobileAccordionIcon} aria-hidden="true">
                                    V
                                </span>
                            </button>
                            <div
                                className={`${mobileAccordionPanel} ${
                                    isOpen ? mobileAccordionPanelOpen : ""
                                }`}
                            >
                                <div className={mobileAccordionPanelInner}>
                                    {section.content}
                                </div>
                            </div>
                        </section>
                    );
                })}
            </div>
        </>
    );
}

const query = graphql`
    query {
        welcomePhotoLandscape: file(relativePath: { eq: "welcome-photo-landscape.jpg" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED, quality: 100, layout: FULL_WIDTH)
            }
        }
        logo: file(relativePath: { eq: "emilka.png" }) {
            childImageSharp {
                gatsbyImageData(placeholder: BLURRED, quality: 80, layout: FULL_WIDTH)
            }
        }
    }
`;
